/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useEffect, useRef, useState } from 'react';
import { ThemeProvider } from '@emotion/react';
import { Helmet } from 'react-helmet-async';
import { isIOS } from 'react-device-detect';
import { Theme, theme as defaultTheme } from '@staffino/primitive-controls';
import {
  PageContext,
  PageStateManager,
  FlowContainer,
  FlowObject,
} from '@staffino/flow-screen';
import { ColorTheme, SettingsContext } from '@staffino/flow-controls';
import { FlowState } from '@staffino/model';
import { CookieConsent } from '../../components';
import MerakiFlowBridge from '../meraki/meraki-flow-bridge';
import CachedFlowBridge from './cached-flow-bridge';
import PreviewFlowBridge from './preview-flow-bridge';

type FlowPageType = {
  flowId: string;
  sessionId?: string;
  targetId: string;
  customerId?: string;
  interactionId?: string;
  source?: string;
  medium?: string;
  reason?: string;
  flowObjects?: FlowObject[];
  // coverImageUrl?: string;
  bridgeId?: string;
  localeId?: string;
  platform?: 'kiosk';
  parameters?: Record<string, string | number | boolean | Date | null>;
  themeColors?: Partial<Theme['colors']>;
  status?: string;
  versionId?: string;
  flowState?: FlowState;
  organization?: any;
};

function bridgeFactory(bridgeId: string, params: any) {
  switch (bridgeId) {
    case 'preview':
      return new PreviewFlowBridge(params);
    case 'meraki':
      return new MerakiFlowBridge(params);
    default:
      return new CachedFlowBridge(params);
  }
}

const FlowPage = ({
  targetId,
  sessionId,
  flowId,
  customerId,
  interactionId,
  source,
  medium,
  reason,
  flowObjects,
  platform,
  localeId,
  themeColors = {},
  parameters = {},
  bridgeId = 'cached',
  status,
  versionId,
  flowState,
  organization,
}: FlowPageType) => {
  const [bridge, setBridge] = useState(null);
  const pageContextRef = useRef({ pageStateManager: new PageStateManager() });
  const controlSettings = {
    contextMenuEventIsClick: platform === 'kiosk' || platform === 'pos',
    disableAutoFocus: isIOS,
  };

  console.log('isIOS', isIOS);

  useEffect(() => {
    setBridge(
      bridge ??
        bridgeFactory(bridgeId, {
          flowId,
          sessionId,
          targetId,
          customerId,
          interactionId,
          source,
          medium,
          reason,
          localeId,
          parameters,
          status,
          objects: flowObjects,
          organization,
          versionId,
          flowState,
        }),
    );
  }, [setBridge, bridgeFactory]);

  return (
    <ThemeProvider theme={defaultTheme}>
      <ColorTheme colors={themeColors}>
        <PageContext.Provider value={pageContextRef.current}>
          <Helmet>
            <link rel="stylesheet" href="/public/calendar.css" />
            <link rel="stylesheet" href="/public/react-popper-tooltip.css" />
            <link rel="stylesheet" href="/public/flow.css" />
            <script src="/public/js/runtime.js" />
            <script src="/public/js/vendors.js" />
            <script src="/public/js/flow.js" />
          </Helmet>
          {[
            'db238a88-db03-48cd-b79e-0e4ae1862e15',
            '909d9244-7623-4faa-b64c-cd6b9e9383b4',
            '76598818-bc32-4080-be6f-59a811b9a08a',
            'e1834e89-e9f7-4f5a-97c4-2a639c320af6',
            '77b28bd5-1b58-43ba-9045-46e2345a63dd',
            '4e63bd2c-f70d-4383-a392-8a86cea25354',
            '39abf843-210b-4bba-9e9c-d092fc120bda',
          ].includes(flowId) && (
            <Helmet>
              {/* dr max tracking  */}
              <script type="text/javascript">
                {`
                    window.smartlook||(function(d) {
                      var o=smartlook=function(){ o.api.push(arguments)},h=d.getElementsByTagName('head')[0];
                      var c=d.createElement('script');o.api=new Array();c.async=true;c.type='text/javascript';
                      c.charset='utf-8';c.src='https://web-sdk.smartlook.com/recorder.js';h.appendChild(c);
                      })(document);
                      smartlook('init', '07188dd659e01bdf282ae86de73826a39d97cb61', { region: 'eu' });`}
              </script>
            </Helmet>
          )}
          {bridge && (
            <SettingsContext.Provider value={controlSettings}>
              <FlowContainer
                bridge={bridge}
                flowId={flowId}
                platform={platform}
              />
            </SettingsContext.Provider>
          )}
          {bridgeId !== 'preview' && platform !== 'kiosk' && platform !== 'pos' &&
            <CookieConsent />
          }
        </PageContext.Provider>
      </ColorTheme>
    </ThemeProvider>
  );
};

export default FlowPage;
